import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useId } from '@fluentui/react-hooks';
import { withDefaultStyles, useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Link } from '@fluentui/react';
import { BingMapsContext } from '../bing-maps-context';
import { getStyles } from './get-styles';
import { BingMapsView } from '../bing-maps-view/index';
import { LocationTarget } from '../util/index';

function BingMapsWithZoomButtonUnstyled(props) {
  const {
    locationTargets,
    negativeLocationTargets,
    mapRef,
    className,
    zoomButtonClassName,
  } = props;

  const { loadBingMapsSdkRequested, Maps, MapsError } = useContext(BingMapsContext);
  const classes = useDefaultStyles(getStyles, props);

  const i18n = useContext(I18nContext);

  const isLoadingMaps = loadBingMapsSdkRequested && !Maps && !MapsError;
  const mapUnavailable = !Maps && !(window.Microsoft && window.Microsoft.Maps && window.Microsoft.Maps.loadModule);

  const announceId = useId('a11yAnnounceArea');
  const a11yAnnounceArea = document.getElementById(announceId);
  const [zoomedOutClicked, setZoomedOutClicked] = useState(0);
  const zoomedOutOnClickLabel = i18n.getString(_TL_('Zoomed out'));

  const handleZoom = useCallback((zoomArgs) => {
    if (mapRef && mapRef.current) {
      mapRef.current.zoom(zoomArgs);
      if (a11yAnnounceArea) {
        a11yAnnounceArea.textContent = '';
      }
      setZoomedOutClicked(zoomedOutClicked + 1);
    }
  }, [a11yAnnounceArea, mapRef, zoomedOutClicked]);

  useEffect(() => {
    if (a11yAnnounceArea && zoomedOutClicked > 0) {
      a11yAnnounceArea.textContent = zoomedOutOnClickLabel;
    }
  }, [a11yAnnounceArea, zoomedOutClicked, zoomedOutOnClickLabel]);

  return (
    <div className={className}>
      <BingMapsView
        id="bing-maps-location-targets-editor-map"
        className="bing-maps-location-targets-editor-map"
        locationTargets={locationTargets}
        negativeLocationTargets={negativeLocationTargets}
        ref={mapRef}
      />
      <div className={zoomButtonClassName}>
        <Link
          className="zoom-to-fit-link"
          onClick={handleZoom}
        >
          {i18n.getString(_TL_('Zoom out to show all locations'))}
        </Link>
        {/* for a11y announce do not remove the div below */}
        <div
          id={announceId}
          className={classNames('a11yAnnounceArea-zoomOut', classes.announceArea)}
          aria-live="polite"
          aria-atomic="true"
        />
      </div>
      {!isLoadingMaps && mapUnavailable && (
        <div className="error">
          {i18n.getString(_TL_('We\'ve encountered an error and map preview is not available.'))}
        </div>
      )}
    </div>
  );
}

BingMapsWithZoomButtonUnstyled.propTypes = {
  locationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)).isRequired,
  negativeLocationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)),
  /**
   * Ref that can be used to trigger actions in the map web control like zooming.
   */
  mapRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.shape({
        zoom: PropTypes.func,
      }),
    }),
  ]).isRequired,
  className: PropTypes.string,
  zoomButtonClassName: PropTypes.string,
};

BingMapsWithZoomButtonUnstyled.defaultProps = {
  negativeLocationTargets: [],
  className: 'bing-maps-location-targets-editor-map-and-zoom-buttons',
  zoomButtonClassName: 'zoom-buttons',
};


export const BingMapsWithZoomButton = withDefaultStyles(BingMapsWithZoomButtonUnstyled, getStyles);
